import React, { useEffect, useState } from "react";
import { Box, Button, Text, Checkbox, IconButton } from "@primer/react";
import { TrashIcon, UploadIcon, FileIcon } from "@primer/octicons-react";
import { useCommonContext } from "../../../contexts/common";
import { giayToNopApi } from "../../../api/giayToNopApi";
import { loaiGiayToApi } from "../../../api/loaiGiayToApi";
import MyModal from "../../../components/ui/modal/MyModal";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import moment from "moment";
import ComboboxGioiTinh from "../../../components/combobox-gioitinh";
import ComboboxTrangThaiGiayTo from "../../../components/combobox-trangthaigiayto";

interface GiayToNopFormProps {
  hocSinh: IHocSinh;
  onUploadedAvatar: () => void;
  canEdit?: boolean;
}

const GiayToNopForm: React.FC<GiayToNopFormProps> = ({
  hocSinh,
  onUploadedAvatar,
  canEdit = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loaiGiayTos, setLoaiGiayTos] = useState<any[]>([]);
  const [giayToNops, setGiayToNops] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [giayToNopChiTietDeltedId, setGiayTopNopChiTietDeletedId] = useState(0);

  const uploadFileBtns = React.useRef<HTMLInputElement[]>([]);
  const { translate } = useCommonContext();

  const tableStyles = {
    table: {
      width: "100%",
      borderCollapse: "collapse" as const,
      fontSize: "14px",
      backgroundColor: "white",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      borderRadius: "8px",
      overflow: "hidden",
    },
    headerCell: {
      padding: "12px",
      backgroundColor: "#f6f8fa",
      borderBottom: "2px solid #e1e4e8",
      color: "#24292e",
      fontWeight: 600,
      textAlign: "left" as const,
      verticalAlign: "middle" as const,
    },
    cell: {
      padding: "12px",
      borderBottom: "1px solid #e1e4e8",
      verticalAlign: "middle" as const,
      lineHeight: "1.4",
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "4px",
    },
    documentTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "8px",
    },
    uploadButton: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px",
      borderRadius: "4px",
      border: "none",
      cursor: "pointer",
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "#f6f8fa",
      },
    },
  };

  const getImageUrl = (url: string) => {
    if (url.includes("1school.s3.cloudfly.vn")) {
      const matches = url.match(
        /1school\.s3\.cloudfly\.vn\/admission\/IMG\/[^\/]+\.[^\/]+$/
      );
      if (matches) {
        return `https://${matches[0]}`;
      }
    }

    if (/^[a-f0-9-]+\.(jpg|jpeg|png|pdf)$/i.test(url)) {
      return `https://1school.s3.cloudfly.vn/admission/IMG/${url}`;
    }

    return url;
  };

  useEffect(() => {
    loadData();
  }, [hocSinh]);

  useEffect(() => {
    const processedData = loaiGiayTos.map((x) => {
      const giayToNop = giayToNops.find((y) => y.dm_loaigiayto_id === x.id);
      return {
        ...x,
        giay_to_nop: giayToNop,
        is_danop: giayToNop ? giayToNop.is_danop : false,
        is_datra: giayToNop ? giayToNop.is_datra : false,
        trangthai_giayto_id: giayToNop ? giayToNop.trangthai_giayto_id : 0,
      };
    });
    setDataSource(processedData);
  }, [loaiGiayTos, giayToNops]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([getLoaiGiayTos(), getGiayToNops()]);
    } finally {
      setIsLoading(false);
    }
  };

  const getLoaiGiayTos = async () => {
    try {
      const res = await loaiGiayToApi.select({
        nam_hoc: hocSinh.nam_hoc,
        dm_truong_id: hocSinh.dm_truong_id,
        dm_khoi_id: hocSinh.dm_khoi_id,
        dm_he_id: hocSinh.dm_he_id,
      });
      if (res.is_success) {
        setLoaiGiayTos(res.data);
      }
    } catch (error) {
      console.error("Get loai giay to error:", error);
    }
  };

  const getGiayToNops = async () => {
    try {
      const res = await giayToNopApi.SelectByHocSinh(hocSinh.id);
      if (res.is_success) {
        const processedData = res.data.map((item: any) => ({
          ...item,
          ts_giaytonop_chitiets: item.ts_giaytonop_chitiets?.map(
            (chitiet: any) => ({
              ...chitiet,
              duong_dan: getImageUrl(chitiet.duong_dan),
            })
          ),
        }));
        setGiayToNops(processedData);
      }
    } catch (error) {
      console.error("Get giay to nop error:", error);
    }
  };

  const StatusCheckbox = ({ item, field, label }: any) => {
    return (
      <div style={tableStyles.checkboxContainer}>
        <Checkbox
          checked={item[field]}
          disabled={!canEdit}
          onChange={(e) => handleStatusChange(item, field, e.target.checked)}
        />
        <span>{label}</span>
      </div>
    );
  };

  const handleStatusChange = async (
    item: any,
    field: string,
    checked: boolean
  ) => {
    if (!canEdit) return;
    try {
      setIsLoading(true);

      const dataToSave = {
        ts_giaytonops: [
          {
            ts_hocsinh_id: hocSinh.id,
            dm_loaigiayto_id: item.id,
            is_danop: field === "is_danop" ? checked : item.is_danop,
            is_datra: field === "is_datra" ? checked : item.is_datra,
          },
        ],
        ts_hocsinh_id: hocSinh.id,
      };

      const res = await giayToNopApi.Inserts(dataToSave);
      if (res.is_success) {
        setDataSource((prevData) =>
          prevData.map((x) =>
            x.id === item.id ? { ...x, [field]: checked } : x
          )
        );
        await getGiayToNops();
      }
    } catch (error) {
      console.error("Save changes error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusPaperChange = async (
    item: any) => {
    if (!canEdit) return;
    try {
      setIsLoading(true);

      const dataToSave = {
        ts_giaytonops: [
          {
            ts_hocsinh_id: hocSinh.id,
            dm_loaigiayto_id: item.id,
            // is_danop: field === "is_danop" ? checked : item.is_danop,
            // is_datra: field === "is_datra" ? checked : item.is_datra,
            trangthai_giayto_id: item.trangthai_giayto_id,
          },
        ],
        ts_hocsinh_id: hocSinh.id,
      };

      const res = await giayToNopApi.Inserts(dataToSave);
      if (res.is_success) {
        // setDataSource((prevData) =>
        //   prevData.map((x) =>
        //     x.id === item.id ? { ...x, [field]: checked } : x
        //   )
        // );
        await getGiayToNops();
      }
    } catch (error) {
      console.error("Save changes error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const FilePreview = ({ file }: { file: any }) => {
    const isImage = /\.(jpg|jpeg|png)$/i.test(file.ten_file);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        {isImage ? (
          <img
            src={file.duong_dan}
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              objectFit: "cover",
            }}
            alt={file.ten_file}
          />
        ) : (
          <FileIcon size={24} />
        )}
        <Text
          as="a"
          href={file.duong_dan}
          target="_blank"
          sx={{
            flex: 1,
            color: "#0366d6",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {file.ten_file}
        </Text>
        {canEdit && (
          <IconButton
            icon={TrashIcon}
            variant="danger"
            size="small"
            onClick={() => setGiayTopNopChiTietDeletedId(file.id)}
            aria-label="Delete"
          />
        )}
      </Box>
    );
  };

  const handleUploadFiles = async (data: any, files: FileList) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append("formFiles", file);
      });

      const res = await giayToNopApi.Upload({
        dm_loaigiayto_id: data.id,
        ts_hocsinh_id: hocSinh.id,
        formFiles: formData,
      });

      if (res.is_success) {
        const updatedGiayToNops = await giayToNopApi.SelectByHocSinh(
          hocSinh.id
        );
        if (updatedGiayToNops.is_success) {
          const processedData = updatedGiayToNops.data.map((item: any) => ({
            ...item,
            ts_giaytonop_chitiets: item.ts_giaytonop_chitiets?.map(
              (chitiet: any) => ({
                ...chitiet,
                duong_dan: getImageUrl(chitiet.duong_dan),
              })
            ),
          }));
          setGiayToNops(processedData);
        }

        if (data.is_avatar) {
          onUploadedAvatar();
        }
      }
    } catch (error) {
      console.error("Upload files error:", error);
    }
  };

  const handleDeleteGiayToChiTiet = async () => {
    try {
      const res = await giayToNopApi.DeleteChiTiet(giayToNopChiTietDeltedId);
      if (res.is_success) {
        await getGiayToNops();
        setGiayTopNopChiTietDeletedId(0);
      }
    } catch (error) {
      console.error("Delete giay to chi tiet error:", error);
    }
  };

  const formatDate = (date: string) => {
    return date ? moment(date).format("DD/MM/YYYY HH:mm") : "-";
  };

  return (
    <Box sx={{ padding: 3 }}>
      {isLoading ? (
        <Box sx={{ textAlign: "center", padding: 4 }}>Loading...</Box>
      ) : (
        <>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.headerCell}>#</th>
                <th style={tableStyles.headerCell}>
                  {translate("Tên giấy tờ")}
                </th>
                <th style={tableStyles.headerCell}>
                  {translate("Trạng thái")}
                </th>
                <th style={tableStyles.headerCell}>{translate("Giấy tờ")}</th>
                <th style={tableStyles.headerCell}>
                  {translate("Ngày nộp bản cứng")}
                </th>
                <th style={tableStyles.headerCell}>
                  {translate("Ngày trả giấy tờ")}
                </th>
              </tr>
            </thead>
            <tbody>
              {dataSource.map((item, index) => (
                <tr key={item.id}>
                  <td style={tableStyles.cell}>{index + 1}</td>
                  <td style={tableStyles.cell}>
                    <div style={tableStyles.documentTitle}>
                      <div>
                        <span style={{ fontWeight: 500 }}>
                          {item.loai_giay_to}
                        </span>
                        {item.huong_dan && (
                          <span
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "#666",
                              marginTop: "4px",
                              fontStyle: "italic",
                            }}
                          >
                            {item.huong_dan}
                          </span>
                        )}
                      </div>
                      <div>
                        <input
                          type="file"
                          multiple
                          accept="image/*,application/pdf"
                          style={{ display: "none" }}
                          disabled={!canEdit}
                          ref={(el) => {
                            if (el) uploadFileBtns.current[item.id] = el;
                          }}
                          onChange={(e) =>
                            e.target.files &&
                            handleUploadFiles(item, e.target.files)
                          }
                        />
                        {canEdit && (
                          <IconButton
                            icon={UploadIcon}
                            size="small"
                            onClick={() =>
                              uploadFileBtns.current[item.id]?.click()
                            }
                            aria-label="Upload"
                          />
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={tableStyles.cell}>
                    {/* <StatusCheckbox
                      item={item}
                      field="is_danop"
                      label="Đã nộp bản cứng"
                    />
                    <StatusCheckbox
                      item={item}
                      field="is_datra"
                      label="Đã trả giấy tờ"
                    /> */}
                    <ComboboxTrangThaiGiayTo
                      value={item.trangthai_giayto_id}
                      onValueChanged={(value) => {
                        if (!canEdit) return;
                        item.trangthai_giayto_id = value;
                        handleStatusPaperChange(item);
                      }}
                    />
                  </td>
                  <td style={tableStyles.cell}>
                    {item.giay_to_nop?.ts_giaytonop_chitiets?.map(
                      (file: any) => (
                        <FilePreview key={file.id} file={file} />
                      )
                    )}
                  </td>
                  <td style={tableStyles.cell}>
                    {formatDate(item.giay_to_nop?.ngaynopbancung)}
                  </td>
                  <td style={tableStyles.cell}>
                    {formatDate(item.giay_to_nop?.ngaytrabancung)}
                    {item.giay_to_nop?.nguoitra && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "12px",
                          color: "#666",
                          marginTop: "4px",
                          fontStyle: "italic",
                        }}
                      >
                        {item.giay_to_nop?.nguoitra}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <MyModal
            isOpen={giayToNopChiTietDeltedId > 0}
            onClose={() => setGiayTopNopChiTietDeletedId(0)}
            title={translate("Lưu ý")}
            width="medium"
          >
            <Box sx={{ p: 3 }}>
              <Text>
                {translate("Bạn có chắc chắn muốn xóa giấy tờ này ?")}
              </Text>
            </Box>
            <Box
              sx={{
                p: 3,
                borderTop: "1px solid #e1e4e8",
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button onClick={() => setGiayTopNopChiTietDeletedId(0)}>
                {translate("Đóng")}
              </Button>
              <Button variant="danger" onClick={handleDeleteGiayToChiTiet}>
                {translate("Xóa giấy tờ")}
              </Button>
            </Box>
          </MyModal>
        </>
      )}
    </Box>
  );
};

const loadingStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
  spinner: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "3px solid #e1e4e8",
    borderTopColor: "#0366d6",
    animation: "spin 1s linear infinite",
  },
};

const globalStyles = `
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default GiayToNopForm;
